import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { EngineController } from "./app/controller";
import { Library } from "./app/library";
import { bindMediaSession } from "./app/mediaSession";
import { createDefaultProject } from "./project/defaults";
import { LocalStore } from "./project/storage";
import { App } from "./ui/App";
import { LAST_PROJECT_KEY, safeStorageGet } from "./ui/prefs";
import { applyTheme, loadTheme } from "./ui/theme";
import "./ui/styles.css";

// Apply the saved theme before first paint to avoid a flash of the wrong colours.
applyTheme(loadTheme());

async function boot() {
  let storage: LocalStore | null = null;
  try { storage = await LocalStore.open(); } catch (err) { console.warn(err); }
  const controller = new EngineController(createDefaultProject(), { storage });
  const library = new Library(controller);
  bindMediaSession(controller, { base: import.meta.env.BASE_URL });
  // Reopen the last project (per-browser convenience; failures fall back to a new project).
  const last = safeStorageGet(LAST_PROJECT_KEY);
  if (last && storage && (await storage.hasProject(last))) await library.open(last);
  // Dev-only hook for end-to-end tests (stripped from production builds by Vite).
  if (import.meta.env.DEV) (window as unknown as { __mp: unknown }).__mp = { controller, library };
  if (!storage) controller.notify("warning", "Local storage is unavailable (private window or blocked site data). You can still play, but projects cannot be saved here — use Export.");
  createRoot(document.getElementById("root")!).render(
    <StrictMode>
      <App controller={controller} library={library} />
    </StrictMode>,
  );
}

void boot();
